/*
    Admin credit-pool management page.

    Lists every shared credit pool and lets an administrator create pools, set their
    credit balance, add/remove members by email, and delete pools. Consumes the
    /credit-pool/admin-* endpoints (PIN-153). Rename is intentionally not offered here:
    the backend has no rename endpoint yet (a name is only set at create time).

    Pools are listed alphabetically by name and can be searched by pool name, owner
    email or member email (PIN-299), so an admin can find an existing pool quickly.
*/

import React, { useCallback, useEffect, useState } from 'react';
import Dashboard from '../dashboard';
import axios from 'axios';
import { useSelector } from 'react-redux';
import cardStyles from '../../style/info-card.module.css';
import styles from '../../style/pools-page.module.css';
import { Button, Input, InputNumber, Modal, Tag, notification } from 'antd';
import history from '../../history';

const apiUrl = process.env.REACT_APP_API_URL;

// Labeled field for the modals, so inputs get a heading and consistent spacing.
const Field = ({ label, children }) => (
  <div className={styles['modal-field']}>
    <label className={styles['modal-field-label']}>{label}</label>
    {children}
  </div>
);

const byName = (a, b) =>
  (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });

// Case-insensitive substring match on the pool name, owner email and member emails.
// `needle` is the already trimmed, lowercased query.
const matchesNeedle = (pool, needle) =>
  [pool.name, pool.owner && pool.owner.email, ...(pool.users || []).map((u) => u.email)]
    .filter(Boolean)
    .some((value) => value.toLowerCase().includes(needle));

const PoolsPage = () => {
  const token = useSelector((state) => state.token);
  const userInfo = useSelector((state) => state.userInfo);
  const [pools, setPools] = useState([]); // kept sorted by name
  const [query, setQuery] = useState('');

  // create-pool modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createOwnerEmail, setCreateOwnerEmail] = useState('');
  const [createMemberEmails, setCreateMemberEmails] = useState('');
  const [createCredits, setCreateCredits] = useState(0);

  // set-credits modal (holds the target pool, or null when closed)
  const [creditsPool, setCreditsPool] = useState(null);
  const [creditsValue, setCreditsValue] = useState(0);

  // add-member modal
  const [addMemberPool, setAddMemberPool] = useState(null);
  const [addMemberEmail, setAddMemberEmail] = useState('');

  // delete-confirm modal
  const [deletePool, setDeletePool] = useState(null);

  // id of the pool currently playing its exit ("crumple") animation
  const [deletingId, setDeletingId] = useState(null);

  // The deleting row stays visible regardless of the search: its onAnimationEnd triggers the
  // refetch, so filtering it out mid-animation would leave the deleted pool in the list.
  const needle = query.trim().toLowerCase();
  const visiblePools = needle
    ? pools.filter((pool) => pool._id === deletingId || matchesNeedle(pool, needle))
    : pools;

  // Redirect non-admin users
  useEffect(() => {
    if (userInfo.kind && userInfo.kind !== 'Administrator') {
      history.push('/');
    }
  }, [userInfo.kind]);

  const postAdmin = useCallback(
    (path, body) =>
      axios.post(apiUrl + path, body, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Token ' + token
        }
      }),
    [token]
  );

  const getPools = useCallback(async () => {
    try {
      const result = await postAdmin('/credit-pool/admin-list', {});
      setPools([...(result.data.creditPools || [])].sort(byName));
    } catch (error) {
      notification.error({
        message: 'Failed to load credit pools',
        description: error.message
      });
    }
  }, [postAdmin]);

  useEffect(() => {
    getPools();
  }, [getPools]);

  const createPool = useCallback(async () => {
    try {
      const memberEmails = createMemberEmails
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean);

      await postAdmin('/credit-pool/admin-create', {
        name: createName,
        ownerEmail: createOwnerEmail,
        memberEmails,
        credits: createCredits
      });

      notification.success({ message: 'Credit pool created' });
      setCreateOpen(false);
      setCreateName('');
      setCreateOwnerEmail('');
      setCreateMemberEmails('');
      setCreateCredits(0);
      // Clear the search so the new pool isn't hidden by it.
      setQuery('');
      await getPools();
    } catch (error) {
      notification.error({
        message: 'Failed to create credit pool',
        description: error.message
      });
    }
  }, [postAdmin, createCredits, createMemberEmails, createName, createOwnerEmail, getPools]);

  const saveCredits = useCallback(async () => {
    try {
      await postAdmin('/credit-pool/admin-set-credits', {
        creditPoolID: creditsPool._id,
        credits: creditsValue
      });
      notification.success({ message: 'Credits updated' });
      setCreditsPool(null);
      await getPools();
    } catch (error) {
      notification.error({
        message: 'Failed to update credits',
        description: error.message
      });
    }
  }, [postAdmin, creditsPool, creditsValue, getPools]);

  const addMember = useCallback(async () => {
    try {
      await postAdmin('/credit-pool/admin-add-user', {
        creditPoolID: addMemberPool._id,
        email: addMemberEmail
      });
      notification.success({ message: 'Member added' });
      setAddMemberPool(null);
      setAddMemberEmail('');
      await getPools();
    } catch (error) {
      notification.error({
        message: 'Failed to add member',
        description: error.message
      });
    }
  }, [addMemberEmail, addMemberPool, postAdmin, getPools]);

  const removeMember = useCallback(
    async (pool, email) => {
      try {
        await postAdmin('/credit-pool/admin-remove-user', {
          creditPoolID: pool._id,
          email
        });
        notification.success({ message: 'Member removed' });
        await getPools();
      } catch (error) {
        notification.error({
          message: 'Failed to remove member',
          description: error.message
        });
      }
    },
    [postAdmin, getPools]
  );

  const confirmDelete = useCallback(async () => {
    const pool = deletePool;
    try {
      await postAdmin('/credit-pool/admin-delete', { creditPoolID: pool._id });
      notification.success({ message: 'Credit pool deleted' });
      setDeletePool(null);
      // Play the exit animation; the list refetch is deferred to onAnimationEnd.
      setDeletingId(pool._id);
    } catch (error) {
      notification.error({
        message: 'Failed to delete credit pool',
        description: error.message
      });
    }
  }, [postAdmin, deletePool]);

  // Runs once the destroyed row's crumple animation finishes.
  const handleDestroyEnd = useCallback(
    async (e) => {
      if (e.target !== e.currentTarget) return; // ignore bubbled child animations
      setDeletingId(null);
      await getPools();
    },
    [getPools]
  );

  return (
    <Dashboard>
      <div className={cardStyles['card']}>
        <div className={cardStyles['header']}>
          <label className={cardStyles['name']}>Credit Pools</label>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            Create Pool
          </Button>
        </div>
        <hr className={cardStyles['horizontal-line']}></hr>

        <div className={styles['pool-search']}>
          <Input
            allowClear
            aria-label="Search pools"
            placeholder="Search by pool name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        {pools.length === 0 ? (
          <div className={styles['empty-state']}>No credit pools yet.</div>
        ) : visiblePools.length === 0 ? (
          <div className={styles['empty-state']}>{`No credit pools match "${query.trim()}".`}</div>
        ) : (
          <div className={styles['pool-list']}>
            {visiblePools.map((pool) => (
              <div
                key={pool._id}
                className={`${styles['pool-row']}${
                  pool._id === deletingId ? ' ' + styles['pool-row-destroying'] : ''
                }`}
                data-testid={`pool-${pool._id}`}
                onAnimationEnd={pool._id === deletingId ? handleDestroyEnd : undefined}
              >
                <label className={styles['pool-name']} data-testid="pool-name">
                  {pool.name}
                </label>

                <span className={styles['pool-owner']} data-testid={`owner-${pool._id}`}>
                  {pool.owner ? pool.owner.email : '—'}
                </span>

                <div className={styles['pool-meta']}>
                  <span>{`Credits: ${pool.credits}`}</span>
                </div>

                <div className={styles['pool-members']}>
                  <span className={styles['pool-members-label']}>Members:</span>
                  {(pool.users || []).length === 0 ? (
                    <span>—</span>
                  ) : (
                    (pool.users || []).map((member) => (
                      <Tag
                        key={member._id || member.email}
                        closable
                        onClose={(e) => {
                          e.preventDefault();
                          removeMember(pool, member.email);
                        }}
                      >
                        {member.email}
                      </Tag>
                    ))
                  )}
                </div>

                <hr className={styles['pool-divider']} />

                <div className={styles['pool-actions']}>
                  <Button
                    onClick={() => {
                      setCreditsPool(pool);
                      setCreditsValue(pool.credits);
                    }}
                  >
                    Set Credits
                  </Button>
                  <Button
                    onClick={() => {
                      setAddMemberPool(pool);
                      setAddMemberEmail('');
                    }}
                  >
                    Add Member
                  </Button>
                  <Button danger onClick={() => setDeletePool(pool)}>
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        title="Create Credit Pool"
        visible={createOpen}
        okText="Create"
        okButtonProps={{ disabled: !createName.trim() || !createOwnerEmail.trim() }}
        onOk={createPool}
        onCancel={() => setCreateOpen(false)}
      >
        <Field label="Pool name">
          <Input
            placeholder="Pool name"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
          />
        </Field>
        <Field label="Owner email">
          <Input
            placeholder="Owner email"
            value={createOwnerEmail}
            onChange={(e) => setCreateOwnerEmail(e.target.value)}
          />
        </Field>
        <Field label="Member emails (comma-separated)">
          <Input
            placeholder="Member emails (comma-separated)"
            value={createMemberEmails}
            onChange={(e) => setCreateMemberEmails(e.target.value)}
          />
        </Field>
        <Field label="Starting credits">
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            placeholder="Starting credits"
            value={createCredits}
            onChange={(value) => setCreateCredits(Number(value) || 0)}
          />
        </Field>
      </Modal>

      <Modal
        title="Set Credits"
        visible={!!creditsPool}
        okText="Save"
        onOk={saveCredits}
        onCancel={() => setCreditsPool(null)}
      >
        <Field label="Credits">
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            placeholder="Credits"
            value={creditsValue}
            onChange={(value) => setCreditsValue(Number(value) || 0)}
          />
        </Field>
      </Modal>

      <Modal
        title="Add Member"
        visible={!!addMemberPool}
        okText="Add"
        onOk={addMember}
        onCancel={() => setAddMemberPool(null)}
      >
        <Field label="Member email">
          <Input
            placeholder="Member email"
            value={addMemberEmail}
            onChange={(e) => setAddMemberEmail(e.target.value)}
          />
        </Field>
      </Modal>

      <Modal
        title="Delete Credit Pool"
        visible={!!deletePool}
        okText="Confirm Delete"
        okButtonProps={{ danger: true }}
        onOk={confirmDelete}
        onCancel={() => setDeletePool(null)}
      >
        {deletePool ? `Delete "${deletePool.name}"? This cannot be undone.` : null}
      </Modal>
    </Dashboard>
  );
};

export default PoolsPage;
